import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { motion } from 'framer-motion';
import { MapPin, Phone, Globe, Share2, Star, Send, MessageCircle, Instagram, Facebook, Youtube } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

export default function Localizacao() {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const queryClient = useQueryClient();

  const { data: churchInfo, isLoading } = useQuery({
    queryKey: ['churchInfo'],
    queryFn: async () => {
      const info = await base44.entities.ChurchInfo.list();
      return info[0] || null;
    }
  });

  const { data: reviews = [] } = useQuery({
    queryKey: ['reviews'],
    queryFn: () => base44.entities.Review.filter({ is_visible: true }, '-created_date', 20)
  });

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me()
  });

  const { data: memberData } = useQuery({
    queryKey: ['memberData', user?.email],
    queryFn: async () => {
      if (!user?.email) return null;
      const members = await base44.entities.Member.filter({ email: user.email });
      return members[0] || null;
    },
    enabled: !!user?.email
  });

  const createReviewMutation = useMutation({
    mutationFn: (data) => base44.entities.Review.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
      setRating(0);
      setComment('');
      setIsAnonymous(false);
      setShowReviewForm(false);
      toast.success('Avaliação enviada com sucesso!');
    }
  });

  const handleShare = async () => {
    if (navigator.share && churchInfo) {
      try {
        await navigator.share({
          title: churchInfo.name,
          text: `Visite ${churchInfo.name}`,
          url: `https://maps.google.com/?q=${churchInfo.latitude},${churchInfo.longitude}`
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    }
  };

  const handleSubmitReview = () => {
    if (rating === 0) {
      toast.error('Por favor, selecione uma nota.');
      return;
    }
    
    const reviewData = {
      rating,
      comment: comment.trim() || undefined,
      is_anonymous: isAnonymous,
      is_visible: true
    };

    // Only include member info if not anonymous
    if (!isAnonymous && memberData) {
      reviewData.member_name = memberData.full_name;
      reviewData.member_id = memberData.id;
    }
    
    createReviewMutation.mutate(reviewData);
  };

  const averageRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : 0;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white pt-8 pb-6 px-4">
        <div className="max-w-lg mx-auto">
          <PageHeader title="Localização" subtitle="Como chegar até nós" showBack={true} />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {/* Map */}
        {churchInfo?.latitude && churchInfo?.longitude && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="h-56 rounded-2xl overflow-hidden shadow-lg mb-6"
          >
            <MapContainer
              center={[churchInfo.latitude, churchInfo.longitude]}
              zoom={16}
              className="h-full w-full"
              scrollWheelZoom={false}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; OpenStreetMap'
              />
              <Marker position={[churchInfo.latitude, churchInfo.longitude]}>
                <Popup>
                  <strong>{churchInfo.name}</strong>
                  <br />
                  {churchInfo.address}
                </Popup>
              </Marker>
            </MapContainer>
          </motion.div>
        )}

        {/* Address & Contact */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl shadow-sm p-4 mb-4"
        >
          <div className="space-y-4">
            {churchInfo?.address && (
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Endereço</h4>
                  <p className="text-sm text-gray-600 mt-0.5">{churchInfo.address}</p>
                </div>
              </div>
            )}

            {churchInfo?.phone && (
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Telefone</h4>
                  <a href={`tel:${churchInfo.phone}`} className="text-sm text-blue-600 mt-0.5 block">
                    {churchInfo.phone}
                  </a>
                </div>
              </div>
            )}

            {churchInfo?.whatsapp && (
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">WhatsApp</h4>
                  <a 
                    href={`https://wa.me/${churchInfo.whatsapp.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-green-600 mt-0.5 block"
                  >
                    {churchInfo.whatsapp}
                  </a>
                </div>
              </div>
            )}

            {churchInfo?.website && (
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Globe className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Site</h4>
                  <a 
                    href={churchInfo.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-purple-600 mt-0.5 block"
                  >
                    {churchInfo.website}
                  </a>
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-2 mt-4 pt-4 border-t">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={handleShare}
            >
              <Share2 className="w-4 h-4 mr-2" />
              Compartilhar
            </Button>
            {churchInfo?.latitude && churchInfo?.longitude && (
              <Button 
                className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                onClick={() => window.open(`https://maps.google.com/?q=${churchInfo.latitude},${churchInfo.longitude}`, '_blank')}
              >
                <MapPin className="w-4 h-4 mr-2" />
                Abrir no Maps
              </Button>
            )}
          </div>
        </motion.div>

        {/* Social Networks */}
        {(churchInfo?.instagram || churchInfo?.facebook || churchInfo?.youtube_channel) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl shadow-sm p-4 mb-4"
          >
            <h4 className="font-semibold text-gray-900 mb-3">Redes Sociais</h4>
            <div className="flex gap-3">
              {churchInfo?.instagram && (
                <a
                  href={churchInfo.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-500 rounded-xl flex items-center justify-center text-white"
                >
                  <Instagram className="w-6 h-6" />
                </a>
              )}
              {churchInfo?.facebook && (
                <a
                  href={churchInfo.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center text-white"
                >
                  <Facebook className="w-6 h-6" />
                </a>
              )}
              {churchInfo?.youtube_channel && (
                <a
                  href={churchInfo.youtube_channel}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-red-600 rounded-xl flex items-center justify-center text-white"
                >
                  <Youtube className="w-6 h-6" />
                </a>
              )}
            </div>
          </motion.div>
        )}

        {/* Reviews */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl shadow-sm p-4"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="font-semibold text-gray-900">Avaliações</h4>
              {reviews.length > 0 && (
                <div className="flex items-center gap-1 mt-1">
                  <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                  <span className="font-semibold">{averageRating}</span>
                  <span className="text-sm text-gray-500">({reviews.length} avaliações)</span>
                </div>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowReviewForm(!showReviewForm)}
            >
              Avaliar
            </Button>
          </div>

          {showReviewForm && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="border-t pt-4 mb-4"
            >
              <div className="flex justify-center gap-2 mb-3">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setRating(star)}
                    className="p-1"
                  >
                    <Star
                      className={`w-8 h-8 ${
                        star <= rating
                          ? 'fill-amber-400 text-amber-400'
                          : 'text-gray-300'
                      }`}
                    />
                  </button>
                ))}
              </div>
              <Textarea
                placeholder="Deixe um comentário (opcional)"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
                className="mb-3"
              />
              
              <div className="flex items-center space-x-2 mb-3">
                <Checkbox
                  id="anonymous-review"
                  checked={isAnonymous}
                  onCheckedChange={setIsAnonymous}
                />
                <label
                  htmlFor="anonymous-review"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  Enviar como anônimo
                </label>
              </div>

              {isAnonymous && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                  <p className="text-xs text-amber-800">
                    ℹ️ Sua avaliação será exibida sem identificação. Apenas administradores poderão ver quem enviou.
                  </p>
                </div>
              )}

              <Button
                className="w-full bg-emerald-500 hover:bg-emerald-600"
                onClick={handleSubmitReview}
                disabled={createReviewMutation.isPending}
              >
                <Send className="w-4 h-4 mr-2" />
                Enviar Avaliação
              </Button>
            </motion.div>
          )}

          {reviews.length > 0 && (
            <div className="space-y-3 mt-4">
              {reviews.slice(0, 5).map((review) => (
                <div key={review.id} className="border-t pt-3">
                  <div className="flex items-center gap-1 mb-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`w-3 h-3 ${
                          star <= review.rating
                            ? 'fill-amber-400 text-amber-400'
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                  {review.comment && (
                    <p className="text-sm text-gray-600">{review.comment}</p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">
                    {review.is_anonymous ? '🔒 Anônimo' : (review.member_name || 'Usuário')}
                  </p>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}